import { useCallback, useEffect, useRef } from "react";

import type { BoardColumn } from "~/shared/types/quote-board-types.ts";

import type { GridCompositeFilterDescriptor } from "../../utils/quote-board-grid-filter-utils.ts";
import {
	areFiltersEqual,
	areSetsEqual,
	cloneCompositeFilterDescriptor,
	collectActiveFilterFields,
	sanitizeCompositeFilterDescriptor,
} from "../../utils/quote-board-grid-filter-utils.ts";
import type { GridStateActions } from "./use-quote-board-grid-state.ts";

export const useQuoteBoardFilterManagement = (
	processedColumns: BoardColumn[],
	activeFilter: GridCompositeFilterDescriptor | undefined,
	actions: GridStateActions,
	hasActiveSearchChips: boolean,
) => {
	const previousColumnsRef = useRef<Set<string>>(new Set());

	// Handle filter state changes
	const handleFilterChange = useCallback(
		(event: { filter?: GridCompositeFilterDescriptor | null }) => {
			const nextFilter = event?.filter ?? undefined;
			const clonedFilter = nextFilter
				? cloneCompositeFilterDescriptor(nextFilter)
				: undefined;
			actions.setControlledFilter(clonedFilter);

			const activeFilterFields = collectActiveFilterFields(clonedFilter);
			actions.setFilteredColumns(activeFilterFields);
		},
		[actions],
	);

	// Clear grid's internal filter state when external search chips are active
	useEffect(() => {
		if (hasActiveSearchChips) {
			actions.setControlledFilter(undefined);
			actions.setFilteredColumns(new Set());
		}
	}, [hasActiveSearchChips, actions]);

	// Detect column removals and clear filter state
	useEffect(() => {
		if (!Array.isArray(processedColumns)) {
			previousColumnsRef.current = new Set();
			return;
		}

		const currentColumnFields = new Set(
			processedColumns
				.map((col) => col?.field)
				.filter(
					(field): field is string =>
						typeof field === "string" && field.length > 0,
				),
		);

		const sanitizedSource = activeFilter
			? cloneCompositeFilterDescriptor(activeFilter)
			: undefined;
		const sanitizedFilter = sanitizedSource
			? sanitizeCompositeFilterDescriptor(sanitizedSource, currentColumnFields)
			: undefined;
		const filterChanged = !areFiltersEqual(activeFilter, sanitizedFilter);

		if (filterChanged) {
			actions.setControlledFilter(sanitizedFilter);
			actions.incrementDataRefreshKey();
		}

		const desiredFilteredColumns = collectActiveFilterFields(sanitizedFilter);
		actions.setFilteredColumns((prev) => {
			if (areSetsEqual(prev, desiredFilteredColumns)) {
				return prev;
			}
			return desiredFilteredColumns;
		});

		previousColumnsRef.current = currentColumnFields;
	}, [processedColumns, activeFilter, actions]);

	return {
		handleFilterChange,
	};
};
